import { GPSStatusType } from '../types';
import { Coordinates, estimateSpeedFromCoordinates } from '../utils/geo';

export interface GPSUpdate {
  speedMs: number | null;
  accuracy: number;
  timestamp: number;
  status: GPSStatusType;
}

export class GeolocationService {
  private watchId: number | null = null;
  private lastCoordinates: Coordinates | null = null;
  private currentStatus: GPSStatusType = 'idle';
  private onUpdateCallback: ((update: GPSUpdate) => void) | null = null;

  public isSupported(): boolean {
    return typeof window !== 'undefined' && 'geolocation' in navigator;
  }

  public getStatus(): GPSStatusType {
    return this.currentStatus;
  }

  /**
   * Start watching geolocation
   */
  public start(onUpdate: (update: GPSUpdate) => void): boolean {
    if (!this.isSupported()) {
      this.currentStatus = 'unavailable';
      onUpdate({
        speedMs: null,
        accuracy: 0,
        timestamp: Date.now(),
        status: 'unavailable',
      });
      return false;
    }

    if (this.watchId !== null) {
      navigator.geolocation.clearWatch(this.watchId);
      this.watchId = null;
    }

    this.onUpdateCallback = onUpdate;
    this.currentStatus = 'requesting';
    this.emitStatus('requesting');

    try {
      this.watchId = navigator.geolocation.watchPosition(
        (position) => this.handleSuccess(position),
        (error) => this.handleError(error),
        {
          enableHighAccuracy: true,
          maximumAge: 150,
          timeout: 8000,
        }
      );
      return true;
    } catch (e) {
      console.error('Failed to start Geolocation watch:', e);
      this.currentStatus = 'unavailable';
      this.emitStatus('unavailable');
      return false;
    }
  }

  /**
   * Stop watching geolocation
   */
  public stop(): void {
    if (this.watchId !== null && this.isSupported()) {
      navigator.geolocation.clearWatch(this.watchId);
      this.watchId = null;
    }
    this.currentStatus = 'idle';
    this.lastCoordinates = null;
  }

  private handleSuccess(position: GeolocationPosition): void {
    const coords = position.coords;
    const timestamp = position.timestamp || Date.now();
    const accuracy = coords.accuracy || 0;

    // Check accuracy
    let status: GPSStatusType = 'ready';
    if (accuracy > 50) {
      status = 'signal_weak';
    }
    this.currentStatus = status;

    let speedMs: number | null = coords.speed;

    // Fallback: If browser does not provide coords.speed, estimate via Haversine
    if (speedMs === null || speedMs === undefined || isNaN(speedMs)) {
      if (this.lastCoordinates) {
        const currentCoords: Coordinates = {
          latitude: coords.latitude,
          longitude: coords.longitude,
          timestamp,
        };
        speedMs = estimateSpeedFromCoordinates(this.lastCoordinates, currentCoords);
        this.lastCoordinates = currentCoords;
      } else {
        this.lastCoordinates = {
          latitude: coords.latitude,
          longitude: coords.longitude,
          timestamp,
        };
        speedMs = 0;
      }
    } else {
      // Keep track of latest coordinates
      this.lastCoordinates = {
        latitude: coords.latitude,
        longitude: coords.longitude,
        timestamp,
      };
    }

    if (this.onUpdateCallback) {
      this.onUpdateCallback({
        speedMs,
        accuracy,
        timestamp,
        status: this.currentStatus,
      });
    }
  }

  private handleError(error: GeolocationPositionError): void {
    switch (error.code) {
      case error.PERMISSION_DENIED:
        this.currentStatus = 'denied';
        break;
      case error.POSITION_UNAVAILABLE:
        this.currentStatus = 'searching';
        break;
      case error.TIMEOUT:
        this.currentStatus = 'searching';
        break;
      default:
        this.currentStatus = 'unavailable';
    }

    this.emitStatus(this.currentStatus);
  }

  private emitStatus(status: GPSStatusType): void {
    if (this.onUpdateCallback) {
      this.onUpdateCallback({
        speedMs: null,
        accuracy: 0,
        timestamp: Date.now(),
        status,
      });
    }
  }
}
